import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  Project,
  ProjectsFilters,
  CreateProjectPayload,
  UpdateProjectPayload,
} from '../types';

interface ProjectsResponse {
  projects: Project[];
}

// ─── Queries ────────────────────────────────────────────────────────────────

export function useProjects(filters: ProjectsFilters = {}) {
  return useQuery({
    queryKey: ['projects', 'list', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.dept) params.set('dept', filters.dept);
      const qs = params.toString();
      const { data } = await api.get<ProjectsResponse>(`/api/projects${qs ? `?${qs}` : ''}`);
      return data.projects ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

export function useProjectMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => {
    // Refresh both the Projects list and the shared dropdown cache used by Tasks.
    queryClient.invalidateQueries({ queryKey: ['projects'] });
  };

  const createProject = useMutation({
    mutationFn: async (payload: CreateProjectPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/projects', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateProject = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateProjectPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/projects/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteProject = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/projects/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createProject, updateProject, deleteProject };
}
